/* ── Product image prefetch ──────────────────────────────────────────────
 * Warms the browser HTTP cache with the product page's gallery images
 * (at the rendition the PDP will actually request) before the visitor
 * clicks a product card, so the PDP gallery appears instantly.
 *
 * Cards opt in via:
 *   data-prefetch-url="/products/handle"        (PDP document prefetch)
 *   data-prefetch-media="url1|url2"             (media URLs, any width)
 *
 * TRIGGER:
 *   'intent'  — prefetch on hover / touchstart / keyboard focus (default).
 *               Costs almost nothing: visitors only touch what interests them.
 *   'visible' — additionally prefetch the FIRST image of every card that
 *               enters the viewport, after window load, when idle.
 *               More aggressive; spends bandwidth on products that may
 *               never be opened. Leave off for mobile-heavy traffic.
 */
(function () {
  'use strict';

  var TRIGGER = 'intent'; /* 'intent' | 'visible' */
  var MAX_IMAGES_PER_PRODUCT = 2; /* first N gallery images on intent */
  var HOVER_DELAY_MS = 65; /* ignore drive-by hovers */

  /* Respect constrained connections */
  var conn = navigator.connection || {};
  if (conn.saveData) return;
  if (/(^|-)2g$/.test(conn.effectiveType || '')) return;

  /* The PDP gallery renders the image snippet with sizes ≈ "(max-width: Npx) Npx, … 100vw"
   * and srcset widths from this list (capped at page width 1440). The browser picks the
   * smallest candidate ≥ layout-slot × devicePixelRatio — mirror that selection here so
   * the prefetched URL is byte-identical to the one the PDP will request. */
  var WIDTH_LIST = [360, 480, 600, 720, 840, 960, 1280, 1440];
  var PDP_MAX_WIDTH = 1280; /* keep in sync with width: 1280 in product-media-card.liquid */

  function targetWidth(imageWidth) {
    /* slot: the sizes attr rounds the viewport UP to the next breakpoint;
       viewports past the last breakpoint fall through to 100vw */
    var vw = window.innerWidth;
    var slot = vw;
    for (var i = 0; i < WIDTH_LIST.length; i++) {
      if (WIDTH_LIST[i] >= vw) { slot = WIDTH_LIST[i]; break; }
    }
    var need = slot * (window.devicePixelRatio || 1);
    /* candidates are capped at the image's intrinsic width */
    var cap = Math.min(imageWidth || Infinity, PDP_MAX_WIDTH);
    var best = null;
    for (var j = 0; j < WIDTH_LIST.length; j++) {
      if (WIDTH_LIST[j] > cap) break;
      best = WIDTH_LIST[j];
      if (WIDTH_LIST[j] >= need) return WIDTH_LIST[j];
    }
    return best || WIDTH_LIST[0];
  }

  function withWidth(url, w) {
    url = url.replace(/([?&])width=\d+/, '$1width=' + w);
    if (!/[?&]width=/.test(url)) url += (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    return url;
  }

  var done = Object.create(null);

  function prefetchImages(card, maxImages) {
    var media = (card.getAttribute('data-prefetch-media') || '').split('|');
    for (var i = 0; i < Math.min(media.length, maxImages); i++) {
      if (!media[i]) continue;
      var parts = media[i].split(',');
      var w = targetWidth(parseInt(parts[1], 10) || 0);
      var url = withWidth(parts[0], w);
      if (done[url]) continue;
      done[url] = true;
      var img = new Image();
      if ('fetchPriority' in img) img.fetchPriority = 'low';
      img.decoding = 'async';
      img.src = url;
    }
  }

  function prefetchDocument(card) {
    var href = card.getAttribute('data-prefetch-url');
    if (!href || done['doc:' + href]) return;
    done['doc:' + href] = true;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = href;
    document.head.appendChild(link);
  }

  function prefetchCard(card, maxImages) {
    prefetchImages(card, maxImages);
    prefetchDocument(card);
  }

  /* ── Intent: hover (debounced) / touch / focus, delegated ── */
  var hoverTimer = null;
  var hoverCard = null;

  document.addEventListener('mouseover', function (e) {
    var card = e.target.closest && e.target.closest('[data-prefetch-media]');
    if (!card || card === hoverCard) return;
    clearTimeout(hoverTimer);
    hoverCard = card;
    hoverTimer = setTimeout(function () { prefetchCard(card, MAX_IMAGES_PER_PRODUCT); }, HOVER_DELAY_MS);
  });
  document.addEventListener('mouseout', function (e) {
    var card = e.target.closest && e.target.closest('[data-prefetch-media]');
    if (card && card === hoverCard) { clearTimeout(hoverTimer); hoverCard = null; }
  });
  /* touchstart also fires when a finger lands to SCROLL — arm a short timer
     and cancel on touchmove so flick-scrolling a collection never prefetches */
  var touchTimer = null;
  document.addEventListener('touchstart', function (e) {
    var card = e.target.closest && e.target.closest('[data-prefetch-media]');
    if (!card) return;
    clearTimeout(touchTimer);
    touchTimer = setTimeout(function () { prefetchCard(card, MAX_IMAGES_PER_PRODUCT); }, 100);
  }, { passive: true });
  document.addEventListener('touchmove', function () { clearTimeout(touchTimer); }, { passive: true });
  document.addEventListener('focusin', function (e) {
    var card = e.target.closest && e.target.closest('[data-prefetch-media]');
    if (card) prefetchCard(card, MAX_IMAGES_PER_PRODUCT);
  });

  /* ── Visible: first image of cards entering the viewport (opt-in) ── */
  if (TRIGGER === 'visible' && 'IntersectionObserver' in window) {
    var startVisible = function () {
      var idle = window.requestIdleCallback || function (cb) { setTimeout(cb, 400); };
      idle(function () {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            prefetchImages(entry.target, 1); /* first image only */
          });
        }, { rootMargin: '120px' });
        document.querySelectorAll('[data-prefetch-media]').forEach(function (el) { io.observe(el); });
      });
    };
    /* never compete with the page's own load */
    if (document.readyState === 'complete') startVisible();
    else window.addEventListener('load', startVisible);
  }
})();
